/**
 * PermisNacelleForm.jsx — Permis de travail en nacelle (Lafarge)
 * Reproduit les blocs du template :
 *   - Type de nacelle (Ciseaux / Télescopique / Louée)
 *   - EPI / Ancrage (point d'ancrage permanent, ancrage trajet)
 *   - Check-list 8 points obligatoires AVANT démarrage
 *   - Plan de secours (cas 1 / cas 2)
 *   - Personnes autorisées
 *   - Délivrance & clôture
 */
import { Truck } from 'lucide-react';
import FormationPicker from '@/components/FormationPicker';

function Checkbox({ checked, onChange, label, testid, className = '' }) {
  return (
    <label className={`flex items-start gap-2 cursor-pointer text-xs ${className}`} data-testid={testid}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-[#DA291C] flex-shrink-0" />
      <span className="leading-snug">{label}</span>
    </label>
  );
}

function NumInput({ value, onChange, testid, suffix, placeholder, width = 'w-20' }) {
  return (
    <div className="inline-flex items-center gap-1">
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} placeholder={placeholder}
        className={`${width} px-2 py-1 text-xs border border-stone-300 focus:border-[#09090B] outline-none font-mono`} />
      {suffix && <span className="text-[10px] text-stone-500 uppercase">{suffix}</span>}
    </div>
  );
}

function SectionTitle({ children, accent = 'bg-[#09090B]' }) {
  return (
    <div className={`${accent} text-white text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2`}>
      {children}
    </div>
  );
}

const CHECKLIST_ITEMS = [
  { key: 'cl_notice', label: 'Notice de la nacelle consultée' },
  { key: 'cl_vgp', label: 'VGP (semestrielle) à jour' },
  { key: 'cl_surveillance', label: "Présence d'une personne au sol en surveillance" },
  { key: 'cl_zone_stable', label: 'Zone de positionnement nacelle identifiée, stable et plane' },
  { key: 'cl_balisage', label: "Balisage physique de la zone d'évolution de la nacelle" },
  { key: 'cl_obstacles_aeriens', label: 'Mise en sécurité comparativement à des obstacles aériens (lignes, tuyaux…)' },
  { key: 'cl_vent_lignes_elec', label: 'Vent < 45 km/h ET distance de sécurité lignes électriques > 5m' },
  { key: 'cl_coactivite', label: 'Suppression de toute co-activité et circulation générant un risque' },
];

export default function PermisNacelleForm({ extra, setExtra, dateDebut, dateFin, titre, lieu, entreprise, compact = false, siteCode = '' }) {
  const allChecked = CHECKLIST_ITEMS.every((it) => extra[it.key]);
  const isSoustraite = !!entreprise && entreprise !== 'Travaux internes';

  return (
    <div className="space-y-4 border-2 border-yellow-600 bg-white" data-testid="permis-nacelle-form">
      <div className="bg-yellow-600 text-white px-4 py-3 flex items-center gap-3">
        <Truck className="w-6 h-6" strokeWidth={2.5} />
        <div>
          <div className="text-2xl font-display font-black tracking-tighter">PERMIS DE TRAVAIL EN NACELLE</div>
          <div className="text-[10px] uppercase tracking-wider opacity-90">PEMP / Plateforme élévatrice mobile de personnes</div>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        {/* En-tête — masqué en mode compact (enfant d'AT, hérités) */}
        {!compact && (
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Travail</div>
            <div className="flex gap-3">
              <span className={`px-2 py-1 border text-[11px] font-bold ${!isSoustraite ? 'bg-[#09090B] text-white border-[#09090B]' : 'bg-white border-stone-300'}`}>Interne</span>
              <span className={`px-2 py-1 border text-[11px] font-bold ${isSoustraite ? 'bg-[#09090B] text-white border-[#09090B]' : 'bg-white border-stone-300'}`}>Sous-traité</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Société (si externe)</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{entreprise || '—'}</div>
          </div>
          <div className="col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Nature de l'opération</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{titre || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Début travaux</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{dateDebut || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Fin travaux</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{dateFin || '—'}</div>
          </div>
        </div>
        )}

        {/* Type de nacelle */}
        <div>
          <SectionTitle>Type de nacelle</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-3 gap-2 bg-yellow-50/30">
            <Checkbox checked={extra.nacelle_ciseaux} onChange={(v) => setExtra('nacelle_ciseaux', v)} label="Nacelle ciseaux (verticale)" testid="pn-ciseaux" />
            <Checkbox checked={extra.nacelle_telescopique} onChange={(v) => setExtra('nacelle_telescopique', v)} label="Nacelle télescopique multidirectionnelle" testid="pn-telescopique" />
            <Checkbox checked={extra.nacelle_louee} onChange={(v) => setExtra('nacelle_louee', v)} label="Nacelle louée" testid="pn-louee" />
          </div>
        </div>

        {/* Ancrage & EPI */}
        <div>
          <SectionTitle>Ancrage & EPI spécifiques</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-stone-50/50">
            <input type="text" value={extra.epi_specifiques || ''} onChange={(e) => setExtra('epi_specifiques', e.target.value)}
              data-testid="pn-epi" placeholder="EPI spécifiques (harnais, longes…)" className="w-full px-2 py-1 border border-stone-300" />
            <Checkbox checked={extra.ancrage_permanent} onChange={(v) => setExtra('ancrage_permanent', v)} label="Point d'ancrage permanent dans le panier (selon notice)" testid="pn-ancrage-perm" />
            <Checkbox checked={extra.ancrage_a_jour} onChange={(v) => setExtra('ancrage_a_jour', v)} label="Point d'ancrage à jour de contrôle < 1 an et conforme" testid="pn-ancrage-aj" />
            <Checkbox checked={extra.ancrage_trajet} onChange={(v) => setExtra('ancrage_trajet', v)} label="Ancrage dès mouvement de la nacelle (trajet / élévation)" testid="pn-ancrage-trajet" />
            <div>Nb personnes max autorisé dans le panier : <NumInput value={extra.nb_pers_max} onChange={(v) => setExtra('nb_pers_max', v)} testid="pn-nb-max" /></div>
          </div>
        </div>

        {/* Check-list */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Check-list de vérification avant démarrage (les 8 OUI obligatoires)</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-1.5 bg-red-50/30">
            {CHECKLIST_ITEMS.map((it) => (
              <Checkbox key={it.key} checked={extra[it.key]} onChange={(v) => setExtra(it.key, v)} label={it.label} testid={`pn-${it.key}`} />
            ))}
            <div className={`mt-2 p-2 text-[11px] font-bold uppercase tracking-wider border ${
              allChecked ? 'bg-green-100 border-green-600 text-green-800' : 'bg-amber-100 border-amber-600 text-amber-800'
            }`}>
              {allChecked ? '✓ Tous les points sont validés — utilisation autorisée' : '⚠ Si un seul OUI non coché : interdiction d\'utiliser la nacelle'}
            </div>
          </div>
        </div>

        {/* Plan de secours */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Plan de secours</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-red-50/30">
            <div>
              <div className="font-bold text-[11px] uppercase tracking-wider mb-1">Cas 1 — Descente normale possible</div>
              <textarea rows={2} value={extra.plan_cas1 || ''} onChange={(e) => setExtra('plan_cas1', e.target.value)}
                data-testid="pn-plan-cas1" className="w-full px-2 py-1 text-xs border border-stone-300"
                placeholder="Décrire la procédure de descente normale" />
            </div>
            <div>
              <div className="font-bold text-[11px] uppercase tracking-wider mb-1">Cas 2 — Impossibilité de manœuvrer la descente de secours</div>
              <textarea rows={2} value={extra.plan_cas2 || ''} onChange={(e) => setExtra('plan_cas2', e.target.value)}
                data-testid="pn-plan-cas2" className="w-full px-2 py-1 text-xs border border-stone-300"
                placeholder="Décrire la procédure d'évacuation d'urgence (pompiers, secours…)" />
            </div>
          </div>
        </div>

        {/* Personnes autorisées + Signataires */}
        <div>
          <SectionTitle>Personnes autorisées (CACES R486) & Signataires</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs">
            <div className="text-[10px] text-stone-500 italic mb-1">Liste des personnes autorisées à utiliser la nacelle (Nom + Société + dernier contrôle harnais)</div>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <input type="text" placeholder={`Personne ${i + 1} — Nom Prénom`} value={(extra.personnes_autorisees || [])[i]?.nom || ''}
                  onChange={(e) => {
                    const list = [...(extra.personnes_autorisees || [])];
                    list[i] = { ...(list[i] || {}), nom: e.target.value };
                    setExtra('personnes_autorisees', list);
                  }}
                  data-testid={`pn-pers-${i}-nom`} className="px-2 py-1 border border-stone-300" />
                <input type="text" placeholder="Société" value={(extra.personnes_autorisees || [])[i]?.societe || ''}
                  onChange={(e) => {
                    const list = [...(extra.personnes_autorisees || [])];
                    list[i] = { ...(list[i] || {}), societe: e.target.value };
                    setExtra('personnes_autorisees', list);
                  }}
                  data-testid={`pn-pers-${i}-societe`} className="px-2 py-1 border border-stone-300" />
                <input type="text" placeholder="Dernier contrôle harnais (date)" value={(extra.personnes_autorisees || [])[i]?.controle || ''}
                  onChange={(e) => {
                    const list = [...(extra.personnes_autorisees || [])];
                    list[i] = { ...(list[i] || {}), controle: e.target.value };
                    setExtra('personnes_autorisees', list);
                  }}
                  data-testid={`pn-pers-${i}-controle`} className="px-2 py-1 border border-stone-300" />
              </div>
            ))}
            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-stone-200">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Chargé de travaux</div>
                <input type="text" value={extra.charge_travaux || ''} onChange={(e) => setExtra('charge_travaux', e.target.value)}
                  data-testid="pn-charge-travaux" className="w-full px-2 py-1.5 border border-stone-300" placeholder="Nom + fonction" />
              </div>
              <div>
                <FormationPicker
                  module="wah"
                  site={siteCode}
                  value={extra.donneur_ordre || ''}
                  onChange={(name) => setExtra('donneur_ordre', name)}
                  testid="pn-donneur"
                  placeholder="Donneur d'ordre Lafarge (validateur Nacelle = WAH)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
